import React, { type ReactNode, useEffect, useId, useMemo, useRef } from "react";

import type { HotkeyRegistryEntry } from "capitalrx-hotkeys";

import { COMPONENT_CLASSES, DISPLAY_NAMES } from "../constants";
import type { HotkeyBadgeProps } from "../types";
import { areMatchesUnique, classNames, formatKey, formatModifier, getPlatform, parseCombo } from "../utils";
import { useHotkeyContext } from "./HotkeyProvider";

export function HotkeyBadge({
  combo,
  placement = "after",
  theme = "default",
  size = "sm",
  className,
  showTooltip = false,
  tooltipContent,
  hideOnMobile = false,
  disabled = false,
  style,
}: HotkeyBadgeProps): ReactNode {
  const kbdKey = useId();
  const platform = getPlatform();
  const { manager } = useHotkeyContext();
  const managerRef = useRef(manager);
  managerRef.current = manager;
  const parsed = useMemo(() => parseCombo(combo), [combo]);
  const modifiers = parsed?.modifiers ?? [];
  const key = parsed?.key ?? "";

  // Memoize hotkey ID lookup with optimized O(1) lookup fallback for large registries
  const hotkeyId = useMemo(() => {
    if (!manager || !parsed) return null;

    // Use fast indexed lookup if available (O(1) for 80-140+ hotkeys)
    if (manager.getHotkeyByCombo) {
      const result = manager.getHotkeyByCombo(combo);
      if (result) return result.id;
      return null;
    }

    // Fallback to linear search if indexed lookup unavailable
    const allHotkeys = manager.getHotkeys?.() || [];
    const normalizedModifiers = modifiers.map((m) => m.toLowerCase()).sort();
    const normalizedKey = key.toLowerCase();

    // Find matching hotkey(s)
    const matches = allHotkeys.filter((entry: HotkeyRegistryEntry) => {
      const entryModifiers = (entry.config?.modifiers || []).map((m: string) => m.toLowerCase()).sort();
      const entryKey = entry.config?.key?.toLowerCase();

      return (
        entryKey === normalizedKey
        && entryModifiers.length === normalizedModifiers.length
        && entryModifiers.every((mod: string, idx: number) => mod === normalizedModifiers[idx])
      );
    });

    // Only return ID if there's exactly one unique match (by priority and scope)
    if (matches.length === 1 || areMatchesUnique(matches)) {
      return matches[0].id;
    }
    return null;
  }, [manager, parsed, modifiers, key]);

  // Find and disable/enable the matching hotkey in the manager
  useEffect(() => {
    const mgr = managerRef.current;
    if (!mgr || !hotkeyId) return;

    if (disabled) {
      mgr.disable?.(hotkeyId);
    } else {
      mgr.enable?.(hotkeyId);
    }

    // CLEANUP: Re-enable hotkey on unmount only if we disabled it
    return () => {
      if (disabled && hotkeyId && mgr) {
        mgr.enable?.(hotkeyId);
      }
    };
  }, [disabled, hotkeyId]);

  // Early return AFTER all hooks
  if (!parsed) return null;

  const badgeClasses = classNames(
    COMPONENT_CLASSES.badge.base,
    COMPONENT_CLASSES.badge.size[size],
    COMPONENT_CLASSES.badge.theme[theme],
    COMPONENT_CLASSES.badge.placement[placement],
    hideOnMobile && COMPONENT_CLASSES.badge.mobile,
    disabled && COMPONENT_CLASSES.badge.disabled,
    className,
  );

  const keys = [...modifiers.map((mod) => formatModifier(mod as import("capitalrx-hotkeys").KeyModifier, { platform })), formatKey(key)];

  /* Use <span> as outer container to avoid nesting <kbd> inside <kbd>
     Individual keys use <kbd> for proper semantic meaning */
  const badge = (
    <span className={badgeClasses} style={style} aria-label={tooltipContent || combo} aria-disabled={disabled} role="group">
      {keys.map((k, index) => (
        <React.Fragment key={`${kbdKey}-${index}`}>
          {index > 0 && platform !== "mac" && <span className="hotkey-badge__separator">+</span>}
          <kbd className="hotkey-badge__key">{k}</kbd>
        </React.Fragment>
      ))}
    </span>
  );

  if (showTooltip && tooltipContent) {
    return (
      <div className="hotkey-badge-tooltip" title={tooltipContent}>
        {badge}
      </div>
    );
  }

  return badge;
}

HotkeyBadge.displayName = DISPLAY_NAMES.HotkeyBadge;
